"use client"

import styled from "@emotion/styled"
import {
  getCarsSearchLeasingManufacturerManufacturerCodeModelModelCode,
  getCarsSearchSaleVehicleNumber,
  LeasingCarDto,
  SaleCarDto,
  SaleCarDtoPaginatedList,
  SearchMetadataResponse,
} from "api"
import { usePathname } from "next/navigation"
import { FC, useEffect, useRef, useState } from "react"
import { FetchMenusQuery, GetFootersQuery } from "../../apollo"
import { mediaQueries } from "../theme/mediaQueries"
import CookieBanner from "./CookieBanner"
import Footer from "./Footer/Footer"
import Header from "./Header/Header"
import WhatsAppWidget from "./WhatsAppWidget"

const StyledMain = styled.div<{ isHomepage: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding-top: 64px;
  ${mediaQueries.desktopM} {
    padding-top: 72px;
  }
  :focus {
    outline: 0;
  }
`

interface LayoutProps {
  locale: string
  children: React.ReactNode
  menu_data: FetchMenusQuery
  footer_data: GetFootersQuery
  searchCars: SaleCarDtoPaginatedList
  carData?: SaleCarDto | LeasingCarDto | null
  metadata: SearchMetadataResponse
}

const Layout: FC<LayoutProps> = ({
  locale,
  children,
  menu_data,
  footer_data,
  carData,
  searchCars,
  metadata,
}) => {
  const mainRef = useRef<HTMLDivElement>(null)
  const pathName = usePathname()
  const showFooter = !pathName.includes("configuration")
  const [resolvedCarData, setResolvedCarData] = useState<
    SaleCarDto | LeasingCarDto | null
  >(carData ?? null)
  const isInitialMount = useRef(true)

  useEffect(() => {
    // On initial mount the layout already received server-fetched carData for
    // the current route, so skip the redundant client refetch. The effect still
    // runs on client-side navigation, where the layout persists and its carData
    // prop becomes stale.
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    let isActive = true

    const fetchCarData = async () => {
      const saleCarPageMatch = pathName.match(/\/cars\/(\d+)/)
      const leasingCarPageMatch = pathName.match(/\/leasing\/([^/]+)/)

      if (saleCarPageMatch) {
        const carId = saleCarPageMatch[1]
        try {
          const carResponse = await getCarsSearchSaleVehicleNumber(
            carId,
            locale,
          )
          if (isActive) {
            setResolvedCarData(carResponse.data.body)
          }
        } catch (error) {
          console.error("Failed to fetch sale car data in layout:", error)
          if (isActive) {
            setResolvedCarData(null)
          }
        }
        return
      }

      if (leasingCarPageMatch && !pathName.includes("/operating-leasing")) {
        const carId = leasingCarPageMatch[1]
        const [manufacturerCode, modelCode] = carId.split("-")
        if (manufacturerCode && modelCode) {
          try {
            const carResponse =
              await getCarsSearchLeasingManufacturerManufacturerCodeModelModelCode(
                manufacturerCode,
                modelCode,
                locale,
              )
            if (isActive) {
              setResolvedCarData(carResponse.data.body)
            }
          } catch (error) {
            console.error("Failed to fetch leasing car data in layout:", error)
            if (isActive) {
              setResolvedCarData(null)
            }
          }
          return
        }
      }

      if (isActive) {
        setResolvedCarData(null)
      }
    }

    fetchCarData()

    return () => {
      isActive = false
    }
  }, [pathName, locale])
  return (
    <>
      <Header
        locale={locale}
        menu_data={menu_data}
        footer_data={footer_data}
        searchCars={searchCars}
        metadata={metadata}
      />
      <>
        <StyledMain
          id="mainContent"
          tabIndex={-1}
          ref={mainRef}
          role="main"
          isHomepage={pathName === "/"}
        >
          {children}
        </StyledMain>
        {showFooter && <Footer footer_data={footer_data} />}
      </>

      <WhatsAppWidget carData={resolvedCarData} />
      <CookieBanner locale={locale} />
    </>
  )
}

export default Layout
